import getRowFormattedLogoData from "../../../../utils/getRowFormettedLogoData";
import { LOGOS_PER_ROW } from "../../../Options/OptionBlocks/Logos/logoLayoutUtils";
import { getLogoAssetUrl } from "../../../Preview/Block/BlockType/BlockParts/logoAssetUrl";

const EMPTY_SLOT_BORDER = 2;

export const logoHasAsset = (logo) => Boolean(logo?.thumbnail || logo?.url);

export const getLogoRows = (items = []) =>
  getRowFormattedLogoData((items || []).filter((item) => item?.logo)).logoItems;

const imageAspect = (images, src) => {
  const entry = images?.get?.(src);
  if (!entry || !(entry.width > 0) || !(entry.height > 0)) return null;
  return entry.height / entry.width;
};

/**
 * Logo.js layout. Legacy rows split the container into equal slots; managed
 * row/grid layouts use 1/5-width slots with gaps and equal-height grid rows.
 * Images keep their natural aspect at slot width; scale is applied around
 * the slot center and offsets shift it like `position: relative`.
 */
export function layoutLogo(ctx) {
  const { ops, hits, options = {}, items, left, width, top, images, blockIndex } = ctx;
  const managed = options.logo_layout === "row" || options.logo_layout === "grid";
  const containerWidth = (width * (Number(options.column_width) || 100)) / 100;
  const containerLeft =
    containerWidth > width ? left : left + (width - containerWidth) / 2;
  const horizontalGap = Math.max(0, Number(options.gap) || 0);
  const logoRows = getLogoRows(items);

  const rows = logoRows.map((row) => {
    const visible = managed ? (row || []).filter(logoHasAsset) : row || [];
    if (!visible.length) return null;
    const count = visible.length;
    const slotWidth = managed
      ? (containerWidth / LOGOS_PER_ROW) -
        (horizontalGap ? (horizontalGap * (LOGOS_PER_ROW - 1)) / LOGOS_PER_ROW : 0)
      : containerWidth / count;
    let startX = containerLeft;
    let step = slotWidth;
    if (managed) {
      const free = containerWidth - (count * slotWidth + (count - 1) * horizontalGap);
      if (free >= 0) {
        const space = free / (count + 1);
        startX = containerLeft + space;
        step = slotWidth + horizontalGap + space;
      } else {
        startX = containerLeft + free / 2;
        step = slotWidth + horizontalGap;
      }
    }
    const slots = visible.map((logo, i) => {
      const hasAsset = logoHasAsset(logo);
      const src = hasAsset ? getLogoAssetUrl(logo) : null;
      const aspect = hasAsset ? imageAspect(images, src) : null;
      const height = hasAsset ? (aspect ? slotWidth * aspect : 0) : EMPTY_SLOT_BORDER * 2;
      return { logo, src, x: startX + i * step, width: slotWidth, height };
    });
    return { slots, height: Math.max(0, ...slots.map((slot) => slot.height)) };
  });

  const placeRow = (row, rowTop, rowHeight) => {
    row.slots.forEach((slot) => {
      if (!slot.src) return;
      const scale = (slot.logo?.scale ?? 80) / 100;
      const offsetX = Number(slot.logo?.offset_x) || 0;
      const offsetY = Number(slot.logo?.offset_y) || 0;
      const slotTop = rowTop + (rowHeight - slot.height) / 2;
      const cx = slot.x + slot.width / 2 + offsetX;
      const cy = slotTop + slot.height / 2 + offsetY;
      const w = slot.width * scale;
      const h = slot.height * scale;
      const rect = { x: cx - w / 2, y: cy - h / 2, width: w, height: h };
      if (h > 0) {
        ops.push({
          kind: "image",
          src: slot.src,
          ...rect,
          top: rect.y,
          bottom: rect.y + rect.height,
        });
      }
      hits?.push({
        ...rect,
        detail: {
          blockIndex,
          row: slot.logo?.row || 0,
          position: slot.logo?.position || 0,
        },
      });
    });
  };

  if (!managed) {
    let y = top;
    rows.forEach((row) => {
      if (!row) return;
      placeRow(row, y, row.height);
      y += row.height;
    });
    return y - top;
  }

  const trackCount = Math.max(logoRows.length, 1);
  const rowGap = Number(options.gapVertical) || 0;
  const rendered = rows.filter(Boolean);
  const trackHeight = Math.max(0, ...rendered.map((row) => row.height));
  rendered.forEach((row, i) => {
    placeRow(row, top + i * (trackHeight + rowGap), trackHeight);
  });
  return trackCount * trackHeight + (trackCount - 1) * rowGap;
}
